
import { usePageTitle } from '../../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function Privacy() {
  usePageTitle('page_title_privacy');
  const { i18n } = useTranslation();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": i18n.language === 'zh' ? "隐私政策 - Koluno" : "Privacy Policy - Koluno",
    "description": i18n.language === 'zh' ? 
      "Koluno隐私政策详细说明我们如何收集、使用和保护您的个人信息。我们承诺保护用户隐私安全。" :
      "Koluno privacy policy details how we collect, use and protect your personal information. We are committed to protecting user privacy and security.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://koluno.com"}/privacy`,
    "dateModified": "2024-12-01",
    "publisher": {
      "@type": "Organization",
      "name": "Koluno"
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title={i18n.language === 'zh' ? "隐私政策 - Koluno" : "Privacy Policy - Koluno"}
        description={i18n.language === 'zh' ? 
          "Koluno隐私政策详细说明我们如何收集、使用和保护您的个人信息。我们承诺保护用户隐私安全。" :
          "Koluno privacy policy details how we collect, use and protect your personal information. We are committed to protecting user privacy and security."
        }
        keywords={i18n.language === 'zh' ? 
          "隐私政策,个人信息保护,数据安全,用户隐私,信息收集" :
          "privacy policy,personal information protection,data security,user privacy,information collection"
        }
        ogTitle={i18n.language === 'zh' ? "隐私政策 - Koluno" : "Privacy Policy - Koluno"}
        ogDescription={i18n.language === 'zh' ? 
          "Koluno隐私政策详细说明我们如何收集、使用和保护您的个人信息。我们承诺保护用户隐私安全。" :
          "Koluno privacy policy details how we collect, use and protect your personal information. We are committed to protecting user privacy and security."
        }
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://koluno.com"}/privacy`}
        structuredData={structuredData}
      />
      
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {i18n.language === 'zh' ? '隐私政策' : 'Privacy Policy'}
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">
              {i18n.language === 'zh' ? 
                '我们重视您的隐私，致力于保护您的个人信息安全' :
                'We value your privacy and are committed to protecting your personal information security'
              }
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '信息收集' : 'Information Collection'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    '我们收集以下类型的信息：' :
                    'We collect the following types of information:'
                  }
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    {i18n.language === 'zh' ? 
                      '个人身份信息：姓名、邮箱地址、联系方式' :
                      'Personal identification information: name, email address, contact information'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '社交媒体信息：平台链接、粉丝数据、内容领域' :
                      'Social media information: platform links, follower data, content fields'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '合作相关信息：合作偏好、结算方式、历史合作经验' :
                      'Collaboration-related information: cooperation preferences, payment methods, historical collaboration experience'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '技术信息：IP地址、浏览器类型、设备信息' :
                      'Technical information: IP address, browser type, device information'
                    }
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '信息使用' : 'Information Usage'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    '我们使用收集的信息用于：' :
                    'We use the collected information for:'
                  }
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    {i18n.language === 'zh' ? 
                      '评估达人申请资格和匹配合适的合作机会' :
                      'Evaluating creator application qualifications and matching suitable collaboration opportunities'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '与您沟通合作事宜和提供客户服务' :
                      'Communicating collaboration matters with you and providing customer service'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '改进我们的服务质量和用户体验' :
                      'Improving our service quality and user experience'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '发送相关的营销信息和平台更新（经您同意）' :
                      'Sending relevant marketing information and platform updates (with your consent)'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '遵守法律法规要求' :
                      'Complying with legal and regulatory requirements'
                    }
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '信息共享' : 'Information Sharing'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    '我们不会出售、交易或转让您的个人信息给第三方，除非：' :
                    'We will not sell, trade, or transfer your personal information to third parties, except:'
                  }
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    {i18n.language === 'zh' ? 
                      '获得您的明确同意' :
                      'With your explicit consent'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '与合作品牌分享必要信息以促成合作（仅限相关信息）' :
                      'Sharing necessary information with partner brands to facilitate collaboration (relevant information only)'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '遵守法律要求或响应法律程序' :
                      'Complying with legal requirements or responding to legal processes'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '保护我们的权利、财产或安全' :
                      'Protecting our rights, property, or safety'
                    }
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '联系我们' : 'Contact Us'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    '如果您对本隐私政策有任何疑问或需要行使您的权利，请通过以下方式联系我们：' :
                    'If you have any questions about this privacy policy or need to exercise your rights, please contact us through:'
                  }
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>{i18n.language === 'zh' ? '邮箱：' : 'Email:'}</strong> business@koluno.com</p>
                  <p><strong>{i18n.language === 'zh' ? '微信：' : 'WeChat:'}</strong> ljy520155</p>
                  <p><strong>Telegram:</strong> @koluno2020</p>
                </div>
              </div>
            </section>

            <div className="border-t border-gray-200 pt-6 mt-8">
              <p className="text-sm text-gray-500 text-center">
                {i18n.language === 'zh' ? 
                  '最后更新时间：2024年12月' :
                  'Last updated: December 2024'
                }
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
